import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const useUrlSearchParams = (key, defaultValue) => {
  const [param, setParam] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(key) || defaultValue;
  });

  // Effect to update URL when param changes
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (param !== defaultValue) {
      urlParams.set(key, param);
    } else {
      urlParams.delete(key);
    }
    const queryString = urlParams.toString();
    const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;

    // Useing pushState if the parameter has changed, otherwise use replaceState
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (newUrl !== currentUrl) {
      if (window.history.state && window.history.state.path === newUrl) {
        window.history.replaceState({ path: newUrl }, '', newUrl);
      } else {
        window.history.pushState({ path: newUrl }, '', newUrl);
      }
    }
  }, [param, key, defaultValue]);

  // Effect to handle popstate events
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      setParam(urlParams.get(key) || defaultValue);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [key, defaultValue]);

  return [param, setParam];
};

useUrlSearchParams.propTypes = {
  key: PropTypes.string.isRequired,
  defaultValue: PropTypes.string.isRequired,
};

export default useUrlSearchParams;
